import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import { useCall, useTwilioUserMediaContext } from '../../customer-call-sdk';
import { getDeviceId } from '../../lib/generateDeviceId';
import { RootState } from '../../store';
import { getSession } from '../../store/auth/actions';
import { AuthState } from '../../store/auth/types';
import { getCustomerFeature, getCustomerNumbers } from '../../store/customer/actions';
import { CustomerState } from '../../store/customer/types';
import useLogout from 'src/hooks/useLogout';
import Messages from '../../config/messages.constants';

const UserSessionLoader: React.FC<React.PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const call = useCall();
    const { requestMediaPermission, audioDeviceId, videoDeviceId } = useTwilioUserMediaContext();

    const logout = useLogout();

    const { error, clearError, refreshedAccessToken } = call;

    const { sessionHasBeenFetched, user } = useSelector<RootState, AuthState>(
        (state) => state.auth,
    );
    const { activeCustomerNumber } = useSelector<RootState, CustomerState>(
        (state) => state.customerDetail,
    );

    // Fetch current user session using access token
    useEffect(() => {
        dispatch(getSession());
        // eslint-disable-next-line
    }, []);

    // Initialize call sdk
    useEffect(() => {
        if (user) {
            const deviceId = getDeviceId();

            if (!deviceId) {
                logout();
                return;
            }

            call.init({
                userId: user.uuid,
                authToken: null,
                deviceId,
                // Mirror the HTTP-401 handling: on a WebSocket auth rejection, show the
                // server-provided message (falling back to the generic session-expired text)
                // and log the user out (which redirects to /login).
                onAuthFailure: (message) => {
                    toast.error(message || Messages.CONNECT_TIMEOUT);
                    //logout(false);
                },
            });
            requestMediaPermission({
                type: 'ideal',
                audioDeviceId: audioDeviceId ?? '',
                videoDeviceId: videoDeviceId ?? '',
            });
        }
    }, [user]);

    // Handle Error Returned by the call SDK
    useEffect(() => {
        if (error) {
            toast.error(error);
            clearError();
        }
    }, [error]);

    // Set Active Number When new list of customer number is fetched
    useEffect(() => {
        if (activeCustomerNumber) {
            call.setActivePhoneNumber(activeCustomerNumber.number);
        }
    }, [activeCustomerNumber]);

    // Handle Access Token Refreshed on Device registration && fetch user detail
    useEffect(() => {
        if (user && refreshedAccessToken) {
            dispatch(getCustomerNumbers(user.uuid));
            dispatch(getCustomerFeature(user.uuid));
        }
    }, [user, refreshedAccessToken]);

    if (!sessionHasBeenFetched) {
        return <div>Loading...</div>;
    }

    return <>{children}</>;
};

export default UserSessionLoader;
